import { Stack, TextField } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { FieldErrors } from "react-hook-form";

import { useDelayedState } from "../../../hooks/use-delayed-state/useDelayedState";
import { TooltipHost } from "../../surfaces/tooltip/TooltipHost";
import { OutsideClickWatch } from "../../watches/OutsideClickWatch";
import { TextFieldClearButton } from "../button/TextFieldClearButton";
import { DropdownOptions } from "./common/DropdownOptions";
import { DropdownOption } from "./DropdownOption";
import { useSearchDropdownStyles } from "./useSearchDropdownStyles";

export interface AutocompleteDropdownProps {
    selectedKey: string | undefined;
    selectedText?: string;
    initialOptions?: DropdownOption[];
    updateInitialOptionsUponTheirChange?: boolean;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    testId?: string;
    tooltip?: string;
    underlined?: boolean;
    minSearchLength?: number;
    errors?: FieldErrors;
    name?: string;
    onChange(key?: string, text?: string): void;
    onSearch(query: string): Promise<DropdownOption[]>;
}

export const AutocompleteDropdown: React.FunctionComponent<AutocompleteDropdownProps> = ({
    selectedKey,
    selectedText,
    initialOptions,
    updateInitialOptionsUponTheirChange,
    label,
    placeholder,
    disabled,
    testId,
    tooltip,
    underlined,
    minSearchLength = 2,
    name,
    errors = {},
    onChange,
    onSearch
}: AutocompleteDropdownProps) => {
    const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>(initialOptions ?? []);
    const [selectedOption, setSelectedOption] = useState<DropdownOption>();

    const [isListOpen, setIsListOpen] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [delayedInput, input, setInput] = useDelayedState<string>("");

    const { listWrapperStyles, itemStyles, closedListStyles } = useSearchDropdownStyles(dropdownOptions.length, 5);

    const optionsPanelStyles =
        isListOpen && (selectedOption || delayedInput.length >= minSearchLength) ? listWrapperStyles : closedListStyles;

    useEffect(() => {
        if (delayedInput) {
            setIsLoading(true);

            onSearch(delayedInput)
                .then((options) => {
                    setDropdownOptions(options);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [delayedInput]);

    useEffect(() => {
        if (updateInitialOptionsUponTheirChange) {
            setDropdownOptions(initialOptions ?? []);

            const selection = initialOptions?.find((item: DropdownOption) => item.key === selectedKey);

            setSelectedOption(selection);
        }
    }, [initialOptions]);

    useEffect(() => {
        const selection = dropdownOptions.find((item: DropdownOption) => item.key === selectedKey);

        setSelectedOption(selection);
    }, [dropdownOptions.length, selectedKey]);

    useEffect(() => {
        if (selectedText && selectedKey) {
            setDropdownOptions([{ key: selectedKey, text: selectedText }]);
        }
    }, [selectedText]);

    const changeInput = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        setInput(value || "");
    };

    const clearInput = () => {
        setInput("");
    };

    const clearOptions = () => {
        setDropdownOptions([]);
    };

    const clearSelection = () => {
        const clearedKey = undefined;
        const clearedText = undefined;

        onChange(clearedKey, clearedText);
        clearOptions();
        clearInput();
    };

    const openList = () => {
        setIsListOpen(true);
    };

    const closeList = () => {
        setIsListOpen(false);
        clearInput();
    };

    const renderTextFieldButtons = () => (
        <>
            {selectedOption && (
                <TextFieldClearButton
                    disabled={disabled}
                    onClick={() => {
                        clearSelection();
                        closeList();
                    }}
                    testId={`${name}-clear`}
                />
            )}
        </>
    );

    return (
        <OutsideClickWatch onOutsideClick={closeList} isWatching={isListOpen}>
            <TooltipHost content={tooltip}>
                <Stack styles={{ root: { display: "block", position: "relative" } }}>
                    <TextField
                        label={label}
                        value={isListOpen ? input : selectedOption?.text}
                        placeholder={selectedOption?.text || placeholder}
                        onClick={openList}
                        onChange={changeInput}
                        onRenderSuffix={renderTextFieldButtons}
                        autoComplete="off"
                        disabled={disabled}
                        data-testId={testId}
                        underlined={!!underlined}
                        styles={{
                            suffix: {
                                backgroundColor: "transparent",
                                padding: 0
                            },
                            fieldGroup: {
                                border: name && errors[name] && "2px solid red"
                            },
                            root: { width: "100%" }
                        }}
                    />
                    <DropdownOptions
                        options={dropdownOptions}
                        isLoading={isLoading}
                        optionsPanelStyles={optionsPanelStyles}
                        itemStyles={itemStyles}
                        onItemSelected={(option) => {
                            onChange(option?.key, option?.text);
                            closeList();
                        }}
                    />
                </Stack>
            </TooltipHost>
        </OutsideClickWatch>
    );
};
